import { useMemo, useState } from 'react'
import { ButtonBase, Stack, Typography } from '@mui/material'
import { BadgeCheckIcon, LightbulbIcon, PanelRightOpenIcon, PencilIcon, XCircleIcon } from 'lucide-react'
import {
  CONTENT_IDEA_ORIGINS,
  CONTENT_IDEA_TYPES,
  CONTENT_INDUSTRIES,
  CONTENT_SOLUTIONS,
  type ContentIdea,
  type CreateContentIdeaRequest,
} from '@repo/types'
import { EMPTY_VALUE } from '@repo/ui'

import { ActionDropdown, EmptyState, StatusChip, Table } from 'components'
import { headerWithTerm } from 'components/display/Table'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { membershipFilter, nullsLastComparator } from 'components/input/filters/tableOperators'
import { CONTENT_IDEA_STATES } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useContentIdeas, useCreateContentIdea, useSetContentIdeaStatus, useUpdateContentIdea } from 'hooks/api'

import { formatIsoDay } from '../GtmTradeShowsPage/tradeShowModel'
import { ContentIdeaDrawer } from './ContentIdeaDrawer'
import { ContentIdeaModal } from './ContentIdeaModal'
import { IdeasFeedBar } from './IdeasFeedBar'
import {
  compareIdeas,
  industryLabel,
  MANAGE_IDEAS_PERMISSION,
  MANAGE_IDEAS_TIP,
  originLabel,
  priorityRank,
  scoreText,
  solutionLabel,
  typeLabel,
} from './contentIdeaModel'

type Row = ContentIdea & { primaryTerm: string | null }
// The filter drawer renders a multiSelect cell once per OPTION with a synthetic row (see
// IntegrationsPage/RoadmapTab.tsx `CellParams`), so list cells render the option label in that mode.
type CellParams = { row: Row; value?: unknown; filter?: boolean }

const membership = (itemsOf: (row: Row) => string[]) => membershipFilter<Row>(itemsOf)
const nullsLast = (rank: (row: Row) => number | null) => nullsLastComparator<Row>(rank)

const label = (text: string) => <Typography variant="body3">{text}</Typography>

/**
 * VRG-740 — GTM → Articles → Ideas: the queue of pieces Vera might write. Priority first, then
 * score (an SEO-derived estimate, ordering only). Auto-fed after every SEO Recommendations
 * recompute (competitor gaps, rising terms with no page, AI answers that cite a competitor and not
 * Vera); the rest are added here or came from the old pipeline (604d920). Click a title for the
 * detail drawer; moves (brief, draft, approve draft, publish, reject) are recorded with who and when.
 * "Start brief" is the selection; the one approval is on the draft.
 */
export const ContentIdeasPanel = ({
  creating,
  onCreatingChange,
}: {
  creating: boolean
  onCreatingChange: (v: boolean) => void
}) => {
  const { open: notify } = useSnackbar()
  const { isHasAccess: canManage } = useCanAccess({ action: MANAGE_IDEAS_PERMISSION })
  const { data, isLoading, error } = useContentIdeas()
  const create = useCreateContentIdea()
  const update = useUpdateContentIdea()
  const move = useSetContentIdeaStatus()
  const [openId, setOpenId] = useState<string | null>(null)
  const [editing, setEditing] = useState<ContentIdea | null>(null)

  const rows: Row[] = useMemo(
    () => [...(data ?? [])].sort(compareIdeas).map((i) => ({ ...i, primaryTerm: i.terms[0]?.term ?? null })),
    [data],
  )
  const fail = (e: unknown) => notify({ type: 'error', text: (e as Error).message })

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Idea',
        renderHeader: headerWithTerm('Idea', 'content-idea'),
        type: 'string',
        width: 360,
        pinned: 'left',
        disableReorder: true,
        renderCell: ({ row }: { row: Row }) => (
          <ButtonBase
            onClick={() => setOpenId(row.id)}
            aria-label={`Open ${row.title}`}
            sx={{
              height: '100%',
              width: '100%',
              justifyContent: 'flex-start',
              textAlign: 'left',
              '&:hover .idea-title': { textDecoration: 'underline' },
            }}
          >
            <Stack justifyContent="center" sx={{ minWidth: 0 }}>
              <Typography className="idea-title" variant="body3" sx={{ fontWeight: 500 }} noWrap title={row.title}>
                {row.title}
              </Typography>
              <Typography variant="body4" color="text.secondary" noWrap>
                {row.primaryTerm ?? 'No target term'}
                {row.terms.length > 1 ? ` +${row.terms.length - 1}` : ''}
              </Typography>
            </Stack>
          </ButtonBase>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(CONTENT_IDEA_STATES),
        width: 150,
        renderCell: ({ row }: { row: Row }) => (
          <Stack justifyContent="center" height="100%">
            <StatusChip statuses={CONTENT_IDEA_STATES} status={row.status} size="small" />
          </Stack>
        ),
      },
      {
        // VRG-741 — a recommendation card from "Suggest 5 more", awaiting Spencer's decision.
        field: 'recommended_at',
        headerName: 'Recommendation',
        renderHeader: headerWithTerm('Recommendation', 'content-recommendation'),
        type: 'string',
        width: 190,
        filterable: false,
        valueGetter: (_v: unknown, row: Row) => (row?.recommendation ? (row.status === 'idea' ? 'To decide' : 'Decided') : ''),
        renderCell: ({ row }: { row: Row }) =>
          row.recommendation ? label(row.status === 'idea' ? 'To decide' : 'Decided') : EMPTY_VALUE,
      },
      {
        field: 'priority',
        headerName: 'Priority',
        type: 'multiSelect',
        options: ['P1', 'P2', 'P3'],
        sortOptions: false,
        width: 120,
        getSortComparator: nullsLast((r) => (r.priority ? priorityRank(r.priority) : null)),
        renderCell: ({ row, value, filter }: CellParams) =>
          filter ? label(String(value)) : row.priority ?? EMPTY_VALUE,
      },
      {
        field: 'score',
        headerName: 'Score',
        // +18px for the info glyph (headerWithTerm; §49 sizes a column by its header).
        renderHeader: headerWithTerm('Score', 'content-idea-score'),
        type: 'string',
        width: 128,
        filterable: false,
        getSortComparator: nullsLast((r) => r.score),
        renderCell: ({ row }: { row: Row }) => scoreText(row.score) ?? EMPTY_VALUE,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'multiSelect',
        options: [...CONTENT_IDEA_TYPES],
        sortOptions: false,
        getOptionLabel: (o: unknown) => typeLabel(String(o)),
        width: 140,
        renderCell: ({ value, row, filter }: CellParams) => label(typeLabel(String(filter ? value : row.type))),
      },
      {
        field: 'origin',
        headerName: 'Origin',
        renderHeader: headerWithTerm('Origin', 'content-idea-origin'),
        type: 'multiSelect',
        options: [...CONTENT_IDEA_ORIGINS],
        sortOptions: false,
        getOptionLabel: (o: unknown) => originLabel(String(o)),
        width: 220,
        renderCell: ({ value, row, filter }: CellParams) => label(originLabel(String(filter ? value : row.origin))),
      },
      {
        field: 'solutions',
        headerName: 'Solutions',
        type: 'multiSelect',
        options: [...CONTENT_SOLUTIONS],
        sortOptions: false,
        getOptionLabel: (o: unknown) => solutionLabel(String(o)),
        filterValueGetter: (_v: unknown, row: Row) => (row ? row.solutions : []),
        filterOperators: membership((r) => r.solutions),
        valueGetter: (_v: unknown, row: Row) => (row ? row.solutions.map(solutionLabel).join(', ') : ''),
        width: 260,
        renderCell: ({ row, value, filter }: CellParams) =>
          filter
            ? label(solutionLabel(String(value)))
            : row.solutions.length
            ? label(row.solutions.map(solutionLabel).join(', '))
            : EMPTY_VALUE,
      },
      {
        field: 'industries',
        headerName: 'Industries',
        type: 'multiSelect',
        options: [...CONTENT_INDUSTRIES],
        sortOptions: false,
        getOptionLabel: (o: unknown) => industryLabel(String(o)),
        filterValueGetter: (_v: unknown, row: Row) => (row ? row.industries : []),
        filterOperators: membership((r) => r.industries),
        valueGetter: (_v: unknown, row: Row) => (row ? row.industries.map(industryLabel).join(', ') : ''),
        width: 220,
        renderCell: ({ row, value, filter }: CellParams) =>
          filter
            ? label(industryLabel(String(value)))
            : row.industries.length
            ? label(row.industries.map(industryLabel).join(', '))
            : EMPTY_VALUE,
      },
      {
        field: 'research',
        headerName: 'Research',
        type: 'string',
        width: 120,
        filterable: false,
        valueGetter: (_v: unknown, row: Row) => (row ? row.sources.length : 0),
        renderCell: ({ row }: { row: Row }) => (row.sources.length ? row.sources.length : EMPTY_VALUE),
      },
      {
        field: 'updated_at',
        headerName: 'Last updated',
        type: 'string',
        width: 150,
        filterable: false,
        renderCell: ({ row }: { row: Row }) => formatIsoDay(row.updated_at.slice(0, 10)),
      },
      {
        field: 'actions',
        headerName: false,
        disableReorder: true,
        sortable: false,
        width: 56,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: Row }) => (
          <ActionDropdown
            items={[
              {
                label: 'Open',
                icon: <PanelRightOpenIcon strokeWidth={1} />,
                onClick: () => setOpenId(row.id),
              },
              {
                label: 'Edit',
                icon: <PencilIcon strokeWidth={1} />,
                disabled: !canManage,
                tooltip: canManage ? undefined : MANAGE_IDEAS_TIP,
                onClick: () => setEditing(row),
              },
              {
                // The drawer offers every move; the row offers the two decisions (§46: state hides).
                label: 'Approve draft',
                icon: <BadgeCheckIcon strokeWidth={1} />,
                visible: row.status === 'draft',
                disabled: !canManage || move.isPending,
                tooltip: canManage ? undefined : MANAGE_IDEAS_TIP,
                onClick: () =>
                  move.mutate(
                    { id: row.id, input: { to: 'approved' } },
                    {
                      onSuccess: () =>
                        notify({
                          type: 'success',
                          text: `Approved the draft of “${row.title}”.`,
                        }),
                      onError: fail,
                    },
                  ),
              },
              {
                label: 'Reject',
                icon: <XCircleIcon strokeWidth={1} />,
                visible: row.status !== 'rejected' && row.status !== 'published',
                disabled: !canManage,
                tooltip: canManage ? undefined : MANAGE_IDEAS_TIP,
                // Rejecting asks for a reason: the drawer holds that form.
                onClick: () => setOpenId(row.id),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, move.isPending],
  )

  const save = (input: CreateContentIdeaRequest) => {
    if (editing) {
      update.mutate(
        { id: editing.id, input },
        {
          onSuccess: () => {
            notify({ type: 'success', text: `Saved “${input.title}”.` })
            setEditing(null)
          },
          onError: fail,
        },
      )
    } else {
      create.mutate(input, {
        onSuccess: (idea) => {
          notify({ type: 'success', text: `Added “${input.title}”.` })
          onCreatingChange(false)
          setOpenId((idea as ContentIdea).id)
        },
        onError: fail,
      })
    }
  }

  return (
    <>
      <IdeasFeedBar canManage={canManage} onOpenIdea={setOpenId} />
      <Table
        fillBottom
        loading={isLoading}
        rows={rows}
        columns={columns}
        getRowId={(row: Row) => row.id}
        exportFilename="content-ideas"
        emptyState={
          <EmptyState
            title={error ? 'Could not load ideas' : data?.length ? 'No ideas match' : 'No ideas yet'}
            text={
              error
                ? (error as Error).message
                : data?.length
                ? 'Clear the search or a filter to see every idea.'
                : 'Ideas arrive after each SEO recompute and each source poll, or add one.'
            }
            icon={<LightbulbIcon strokeWidth={1} size={32} />}
          />
        }
      />
      <ContentIdeaDrawer
        ideaId={openId}
        canEdit={canManage}
        onEdit={(i) => setEditing(i)}
        onClose={() => setOpenId(null)}
      />
      <ContentIdeaModal
        open={creating || !!editing}
        idea={editing}
        loading={create.isPending || update.isPending}
        onClose={() => (editing ? setEditing(null) : onCreatingChange(false))}
        onSave={save}
      />
    </>
  )
}
